import React, { useState, useEffect } from "react";
import { GoVerified, GoCalendar, GoLocation } from "react-icons/go";
import { FiClock, FiBook, FiAward, FiDollarSign, FiLoader } from "react-icons/fi";
import parse from "html-react-parser";
import CourseInquire from "../common/CourseInquire";
import Cookies from 'js-cookie';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const CourseDetailHero = ({ courseDetails }) => {
  const [model, setModel] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const isMobile = window.innerWidth <= 767;
  const token = Cookies.get("token");
  const [userId, setUserId] = useState('');

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(date);
  };

  // Daily schedule is now from learnAbout field
  const dailySchedule = courseDetails?.Course?.learnAbout || "";
  
  // Extract days from learnAbout HTML
  const extractDailySchedule = (htmlContent) => {
    if (!htmlContent) return [];
    
    // Parse HTML to extract day-wise content
    const days = [];
    const dayPattern = /Day\s+\d+\s*[-–]\s*([^<]+)/gi;
    const matches = htmlContent.matchAll(dayPattern);
    
    let match;
    while ((match = dayPattern.exec(htmlContent)) !== null) {
      days.push({
        title: match[0],
        content: ""
      });
    }
    
    return days.length > 0 ? days : [];
  };

  const parsedDailySchedule = extractDailySchedule(dailySchedule);

  // Course detail cards data
  const courseInfoCards = [
    {
      icon: <FiBook className="text-primary" size={20} />,
      label: "Course Type",
      value: courseDetails?.Course?.courseType
    },
    {
      icon: <GoCalendar className="text-primary" size={20} />,
      label: "Start Date",
      value: formatDate(courseDetails?.Course?.courseStartDate)
    },
    {
      icon: <FiClock className="text-primary" size={20} />,
      label: "Duration",
      value: courseDetails?.Course?.courseDuration
    },
    {
      icon: <FiAward className="text-primary" size={20} />,
      label: "Drone Type",
      value: courseDetails?.Course?.droneType
    },
    {
      icon: <FiAward className="text-primary" size={20} />,
      label: "Category",
      value: courseDetails?.Course?.droneCategory
    }
  ];

  useEffect(() => {
    const storedUserId = Cookies.get("userId");
    console.log("User ID from cookies:", storedUserId);
    if (storedUserId) {
      setUserId(storedUserId);
    }
  }, []);

  // Check for payment status in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const merchantOrderId = urlParams.get('merchantOrderId');
    const courseId = urlParams.get('courseId');
    const userIdParam = urlParams.get('userId');
    const status = urlParams.get('status');

    if (merchantOrderId && courseId) {
      if (status === 'success') {
        toast.success("🎉 Payment successful! Your enrollment is confirmed.");
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (status === 'failure') {
        toast.error("❌ Payment failed. Please try again.");
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);

  const handlePayment = async () => {
    console.log("Payment button clicked");
    
    if (!token) {
      toast.error("Please login to enroll in the course");
      return;
    }

    if (!userId) {
      toast.error("User ID not found. Please login again.");
      return;
    }

    const courseId = courseDetails?.Course?._id;
    console.log("Course ID:", courseId);
    console.log("User ID:", userId);

    if (!courseId) {
      toast.error("Course ID not found");
      return;
    }

    setPaymentLoading(true);
    setIsProcessing(true);

    try {
      // Fixed amount of 500 INR
      const paymentAmount = 500;

      // Get current URL for return and webhook
      const baseUrl = window.location.origin;
      const currentPath = window.location.pathname;
      const returnURL = `${baseUrl}${currentPath}`;
      const paymentWebhook = `${process.env.REACT_APP_API_URL} + /payment-webhook`;

      const payload = {
        courseId: courseId,
        userId: userId,
        returnURL: `${returnURL}?status=success`,
        paymentWebhook: paymentWebhook,
        amount: paymentAmount
      };

      console.log("Payment payload to backend:", payload);

      // Make API call to create training order
      const response = await axios.post(
       process.env.REACT_APP_API_URL + "/createTrainingOrder",
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          timeout: 10000 // 10 second timeout
        }
      );

      console.log("Payment API response:", response.data);

      if (response.data.status === "success" && response.data.data.redirectURI) {
        // Redirect to PhonePe payment page
        toast.info("Redirecting to payment gateway...");
        setTimeout(() => {
          window.location.href = response.data.data.redirectURI;
        }, 1000);
      } else {
        throw new Error(response.data.message || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment error details:", error);
      
      let errorMessage = "Failed to process payment. Please try again.";
      
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
        errorMessage = error.response.data?.message || errorMessage;
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received:", error.request);
        errorMessage = "No response from server. Please check your connection.";
      } else {
        // Something happened in setting up the request
        console.error("Request setup error:", error.message);
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setIsProcessing(false);
      setPaymentLoading(false);
    }
  };

  return (
    <div className="py-5 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Content */}
          <div className="w-full lg:w-8/12">
            {/* Video Section */}
            <div className="rounded-xl overflow-hidden shadow-md">
              <video
                className="w-full h-auto max-h-[500px] object-cover"
                loop
                autoPlay
                muted
              >
                <source
                  src={require("../../assets/video/boy-with-high-visibility-harness-pilots-drone-safe-2023-11-27-04-58-53-utc.mp4")}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Course Details Section */}
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Course Details</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {courseInfoCards.map((card, index) => (
                  <div 
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      {card.icon}
                      <div>
                        <p className="text-sm text-gray-500">{card.label}</p>
                        <p className="font-medium text-gray-800">{card.value || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Course Requirements */}
            <div className="mt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Course Eligibility/Requirements</h3>
              <div className="space-y-3">
                {courseDetails?.Course?.courseRequirements ? (
                  parse(courseDetails.Course.courseRequirements)
                ) : (
                  <p className="text-gray-600">No requirements available</p>
                )}
              </div>
            </div>

            {/* What You Will Learn */}
            <div className="mt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">What You Will Learn</h3>
              <div className="prose prose-gray max-w-none">
                {parse(courseDetails?.Course?.learnAbout || "No learning objectives provided")}
              </div>
            </div>

            {/* Daily Schedule */}
            {parsedDailySchedule.length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Course Schedule</h3>
                <div className="space-y-4">
                  {parsedDailySchedule.map((day, index) => (
                    <div 
                      key={index} 
                      className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
                    >
                      <h4 className="font-bold text-gray-800">{day.title}</h4>
                      <p className="text-gray-600 mt-2">{day.content || "Detailed schedule will be provided during the course"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="w-full lg:w-4/12 space-y-6">
            {/* Course Provider */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 mb-3">COURSE PROVIDER</h3>
              <div className="flex items-center gap-4">
                <img
                  src={courseDetails?.Institute?.image}
                  alt="Institute"
                  className="w-16 h-16 rounded-lg object-cover border"
                />
                <div>
                  <div className="flex items-center gap-1">
                    <h4 className="font-bold text-gray-800">
                      {courseDetails?.Institute?.instituteName}
                    </h4>
                    <GoVerified className="text-blue-500" />
                  </div>
                  <p className="text-sm text-gray-500">Official Training Partner</p>
                </div>
              </div>
            </div>

            {/* Course Summary */}
            <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100 sticky top-20">
              <div className="space-y-4">
                <img
                  src={courseDetails?.Course?.image}
                  alt="Course"
                  className="w-full h-40 object-cover rounded-lg"
                />
                <h3 className="text-xl font-bold text-gray-800">{courseDetails?.Course?.courseName}</h3>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-600">
                    <GoLocation size={16} />
                    <span>{courseDetails?.Course?.locationName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <GoCalendar size={16} />
                    <span>{formatDate(courseDetails?.Course?.courseStartDate)}</span>
                  </div>
                </div>

                {/* Who Should Attend */}
                {courseDetails?.Course?.whoShouldGetThisCourse?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Who Should Attend</h4>
                    <div className="flex flex-wrap gap-2">
                      {courseDetails.Course.whoShouldGetThisCourse.map((item, index) => (
                        <span 
                          key={index}
                          className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-sm font-semibold text-gray-500">COURSE FEE</h4>
                    <span className="text-xs text-gray-500">Full Course</span>
                  </div>
                  <div className="flex items-end">
                    <span className="text-2xl font-bold text-gray-800">
                      ₹{new Intl.NumberFormat('en-IN').format(courseDetails?.Course?.price)}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">+ 18% GST</span>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="text-sm font-semibold text-green-600">REGISTRATION FEE</h4>
                      <span className="text-xs text-green-600">To Secure Your Seat</span>
                    </div>
                    <div className="flex items-end">
                      <span className="text-xl font-bold text-green-700">₹500</span>
                      <span className="text-gray-500 text-sm ml-2">(non-refundable)</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Pay now to confirm enrollment. Balance payable at institute.
                    </p>
                  </div>
                </div>

                {/* Payment Buttons */}
                <div className="space-y-3">
                  {/* Pay Now Button */}
                  <button
                    onClick={handlePayment}
                    disabled={isProcessing || paymentLoading}
                    className={`w-full ${
                      paymentLoading || isProcessing 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-green-600 hover:bg-green-700'
                    } text-white py-3 px-5 rounded-md font-medium text-sm transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2`}
                  >
                    {paymentLoading ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Processing Payment...
                      </>
                    ) : isProcessing ? (
                      <>
                        <FiLoader className="animate-spin" />
                        Initiating Payment...
                      </>
                    ) : (
                      <>
                        <FiDollarSign />
                        Pay Registration Fee (₹500)
                      </>
                    )}
                  </button>

                  {/* Enquire Now Button */}
                  <button
                    onClick={() => setModel(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-5 rounded-md font-medium text-sm transition-all shadow-sm hover:shadow-md"
                  >
                    Enquire Now
                  </button>

                  <div className="text-xs text-gray-500 space-y-1">
                    <p className="flex items-center gap-1">
                      <span className="text-green-600">✓</span> Secure your seat with ₹500 registration
                    </p>
                    <p className="flex items-center gap-1">
                      <span className="text-green-600">✓</span> Balance payment at institute
                    </p>
                    <p className="flex items-center gap-1">
                      <span className="text-green-600">✓</span> DGCA approved training
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      <CourseInquire model={model} setModel={setModel} />
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default CourseDetailHero;